import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { environment } from '@/environments/environment';
import { Observable, firstValueFrom } from 'rxjs';
import type { User } from '@core/models/user.model';

export type LoginOptions = {
    inviteToken?: string | null;
    loginHint?: string | null;
};

export type InviteDetails = {
    name?: string | null;
    email: string;
    expires_at: string;
    status: string;
};

export type UserPermissionLevel = 'admin' | 'view';
type LogoutResponse = {
    message?: string;
    logout_url?: string | null;
    post_logout_redirect?: string | null;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly userKey = 'auth_user';
    private readonly authConfig: AuthConfig = {
        issuer: environment.oauth.issuer,
        redirectUri: window.location.origin + environment.auth.callbackPath,
        responseType: environment.oauth.responseType,
        scope: environment.oauth.scope,
        strictDiscoveryDocumentValidation: false
    };

    constructor(
        private readonly http: HttpClient,
        private readonly oauthService: OAuthService
    ) {
        this.oauthService.setStorage(localStorage);
        this.oauthService.configure(this.authConfig);
    }

    login(options: LoginOptions = {}): void {
        const params = new URLSearchParams({
            mode: 'spa',
            callback: this.getCallbackUrl(),
        });

        if (options.inviteToken) {
            params.set('invite_token', options.inviteToken);
        }

        if (options.loginHint) {
            params.set('login_hint', options.loginHint);
        }

        const loginUrl = `${environment.apiBaseUrl}${environment.auth.loginPath}?${params.toString()}`;
        window.location.href = loginUrl;
    }

    getInviteDetails(token: string): Observable<{ data: InviteDetails }> {
        return this.http.get<{ data: InviteDetails }>(`${environment.apiBaseUrl}/auth/invites/${encodeURIComponent(token)}`);
    }

    async refreshToken(): Promise<string | null> {
        const url = `${environment.apiBaseUrl}${environment.auth.refreshPath}`;
        const response = await firstValueFrom(this.http.post<{ access_token?: string }>(url, {}));
        const token = response?.access_token ?? null;

        if (token) {
            this.setAccessToken(token);
        }

        return token;
    }

    async logout(): Promise<void> {
        const url = `${environment.apiBaseUrl}${environment.auth.logoutPath}`;
        let response: LogoutResponse | null = null;

        try {
            response = await firstValueFrom(this.http.post<LogoutResponse>(url, {}));
        } finally {
            this.clearAccessToken();
            this.clearCurrentUser();

            const redirectTarget = response?.logout_url
                || response?.post_logout_redirect
                || environment.auth.postLogoutRedirect;

            window.location.href = redirectTarget;
        }
    }

    getAccessToken(): string | null {
        return this.oauthService.getAccessToken() || localStorage.getItem('access_token');
    }

    setAccessToken(token: string): void {
        localStorage.setItem('access_token', token);
    }

    clearAccessToken(): void {
        this.oauthService.logOut(false);
        localStorage.removeItem('access_token');
    }

    hasAccessToken(): boolean {
        return Boolean(this.getAccessToken());
    }

    getCurrentUser(): User | null {
        const raw = localStorage.getItem(this.userKey);
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw) as User;
        } catch {
            return null;
        }
    }

    getPermission(areaCode: string): UserPermissionLevel | null {
        const normalizedAreaCode = areaCode.trim().toLowerCase();
        if (!normalizedAreaCode) {
            return null;
        }

        const permissions = this.getCurrentUser()?.permissions ?? {};
        const values = permissions[normalizedAreaCode];

        if (!Array.isArray(values) || values.length === 0) {
            return null;
        }

        if (values.some((value) => value?.toLowerCase() === 'admin')) {
            return 'admin';
        }

        if (values.some((value) => value?.toLowerCase() === 'view')) {
            return 'view';
        }

        return null;
    }

    setCurrentUser(user: User | null): void {
        if (!user) {
            this.clearCurrentUser();
            return;
        }
        localStorage.setItem(this.userKey, JSON.stringify(user));
    }

    clearCurrentUser(): void {
        localStorage.removeItem(this.userKey);
    }

    async fetchCurrentUser(): Promise<User | null> {
        const url = `${environment.apiBaseUrl}/me`;
        const response = await firstValueFrom(this.http.get<{ user?: User }>(url));
        const user = response?.user ?? null;
        this.setCurrentUser(user);
        return user;
    }

    getCallbackUrl(): string {
        return `${window.location.origin}${environment.auth.callbackPath}`;
    }
}
